import type { JunkItemVolumeCode } from "../../shared/api/account/types";
import { BugsnagSafe } from "../../shared/utils/bugsnag-safe";

/**
 * The kind-of-items tile values → the codes the lead meta API takes (CID-7928).
 *
 * Keyed by the Sanity option `value` — NOT its `label`. Each option carries both: `label` is the tile
 * text editors may change freely, `value` is read-only in the production Studio ("tied to code",
 * apps/sanity-studio/schemas/fieldOption.ts). The value happens to read like a label, but it is the
 * stable identifier: the funnel writes it to `appointment.notes.typeOfJunk`, and the icon map and the
 * confirmation screen key on it too. The translation to a code happens here, in the app that owns the
 * options; the server accepts codes only.
 */
const CODE_BY_TILE: Readonly<Record<string, JunkItemVolumeCode>> = {
  "Single Item": "single_item",
  "A Few Items": "few_items",
  "Room Full of Items": "room_full_of_items",
  "Full House/Office Clean Up": "full_house_office_cleanup",
};

/** Case, spacing and punctuation never change which tile a value names. */
const normalize = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const CODE_BY_NORMALIZED_TILE = new Map(
  Object.entries(CODE_BY_TILE).map(([tile, code]) => [normalize(tile), code]),
);

/**
 * The code for a `typeOfJunk` value, or null for a tile this map does not know. Once
 * appointmentCreate lands, the store holds php-api's echo of the appointment, where the note carries
 * an audit stamp — "A Few Items [Oct 2, 2026 … by …]" — which is dropped first, the way the
 * confirmation screen drops it.
 */
export const toJunkItemVolumeCode = (
  typeOfJunk: string,
): JunkItemVolumeCode | null =>
  CODE_BY_NORMALIZED_TILE.get(normalize(typeOfJunk.split(" [")[0])) ?? null;

const reportedUnmapped = new Set<string>();

/**
 * A tile renamed in Sanity without updating {@link CODE_BY_TILE}: nothing is saved for it, so say so —
 * once per value, as the contract that calls this re-runs on every later step.
 */
export const reportUnmappedJunkItemVolume = (typeOfJunk: string) => {
  if (reportedUnmapped.has(typeOfJunk)) return;
  reportedUnmapped.add(typeOfJunk);
  BugsnagSafe.notify(
    new Error(
      `Unmapped kind-of-items value "${typeOfJunk}" — the lead's item volume was not saved (CID-7928)`,
    ),
  );
};
